// components/SucursalesMercadoLibre/CajaForm.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Grid,
  Typography,
  Paper,
  InputAdornment,
  Alert,
  Autocomplete,
} from '@mui/material';
import {
  PointOfSale,
  Storefront,
} from '@mui/icons-material';
import { sucursalLocalApi } from './sucursalLocalApi';

const CajaForm = ({ onCreate, onCancel, selectedSucursal, formatearExternalId, empresa }) => {
  const [formData, setFormData] = useState({
    name: '',
    external_id: '',
    store_id: '',
    external_store_id: '',
  });
  const [sucursales, setSucursales] = useState([]);
  const [sucursalSeleccionada, setSucursalSeleccionada] = useState(null);
  const [loadingSucursales, setLoadingSucursales] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Cargar sucursales desde tu BD local
  useEffect(() => {
    cargarSucursalesLocales();
  }, []);

  // Cuando llega una sucursal seleccionada desde el padre, actualizar el formulario
  useEffect(() => {
    if (selectedSucursal && selectedSucursal.id && sucursales.length > 0) {
      // Buscar la sucursal completa en la lista cargada por mp_store_id o id local
      const sucursalEncontrada = sucursales.find(s => 
        s.mp_store_id === String(selectedSucursal.id) || 
        s.id === selectedSucursal.id ||
        s.mp_store_id === selectedSucursal.mp_store_id
      );
      
      if (sucursalEncontrada) {
        setSucursalSeleccionada(sucursalEncontrada);
        setFormData(prev => ({
          ...prev,
          store_id: sucursalEncontrada.mp_store_id,
          external_store_id: sucursalEncontrada.external_id
        }));
      } else if (selectedSucursal.mp_store_id) {
        // Si no está en la lista pero tiene mp_store_id, crear un objeto temporal
        const sucursalTemp = {
          id: selectedSucursal.id,
          mp_store_id: selectedSucursal.mp_store_id,
          nombre: selectedSucursal.name || selectedSucursal.nombre,
          external_id: selectedSucursal.external_id
        };
        setSucursalSeleccionada(sucursalTemp);
        setFormData(prev => ({
          ...prev,
          store_id: sucursalTemp.mp_store_id,
          external_store_id: sucursalTemp.external_id
        }));
      }
    }
  }, [selectedSucursal, sucursales]);

  const cargarSucursalesLocales = async () => {
    setLoadingSucursales(true);
    try {
      const response = await sucursalLocalApi.obtenerSucursalesLocal(empresa);
      setSucursales(response.data || []);
    } catch (error) {
      console.error('Error al cargar sucursales locales:', error);
    } finally {
      setLoadingSucursales(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: value 
    }));
    
    if (name === 'name') {
      setFormData(prev => ({ 
        ...prev, 
        external_id: formatearExternalId(value) 
      }));
    }

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Cuando se selecciona una sucursal en el Autocomplete
  const handleSucursalChange = (event, newValue) => {
    setSucursalSeleccionada(newValue);
    
    if (newValue) {
      setFormData(prev => ({ 
        ...prev, 
        store_id: newValue.mp_store_id,
        external_store_id: newValue.external_id
      }));
    } else {
      setFormData(prev => ({ 
        ...prev, 
        store_id: '',
        external_store_id: ''
      }));
    }
    
    if (errors.store_id) {
      setErrors(prev => ({ ...prev, store_id: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'El nombre de la caja es requerido';
    } else if (formData.name.length < 3) {
      newErrors.name = 'El nombre debe tener al menos 3 caracteres';
    }
    
    if (!formData.store_id) {
      newErrors.store_id = 'Debes seleccionar una sucursal';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setSubmitting(true);
    
    try {
      // Payload fijo para Mercado Pago QR - valores hardcodeados
      const cajaData = {
        name: formData.name.trim(),
        external_id: formData.external_id || formatearExternalId(formData.name),
        store_id: parseInt(formData.store_id),
        external_store_id: formData.external_store_id,
        fixed_amount: true,
        category: 0
      };
      
      
      await onCreate(cajaData);
      
      // Resetear formulario
      setFormData({
        name: '',
        external_id: '',
        store_id: '',
        external_store_id: '',
      });
      setSucursalSeleccionada(null);
      
    } catch (err) {
      console.error('Error al crear caja:', err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Paper elevation={0} sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 1 }}>
        <PointOfSale sx={{ color: '#009ee3', fontSize: 28 }} />
        <Typography variant="h5" fontWeight="600">
          Nueva Caja
        </Typography>
      </Box>

      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          {/* Selección de Sucursal con Autocomplete */}
          <Grid item xs={12}>
            <Autocomplete
              fullWidth
              size="small"
              options={sucursales}
              loading={loadingSucursales}
              value={sucursalSeleccionada}
              onChange={handleSucursalChange}
              getOptionLabel={(option) => {
                if (typeof option === 'object') {
                  return `${option.nombre} (ID: ${option.external_id})`;
                }
                return '';
              }}
              isOptionEqualToValue={(option, value) => 
                option?.mp_store_id === value?.mp_store_id || option?.id === value?.id
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  required
                  label="Sucursal *"
                  placeholder="Buscar sucursal..."
                  error={!!errors.store_id}
                  helperText={errors.store_id || "Selecciona la sucursal donde pertenece la caja"}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start">
                          <Storefront sx={{ color: '#009ee3' }} />
                        </InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Grid>

          {/* Nombre de la caja */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              size='small'
              label="Nombre de la caja *"
              name="name"
              value={formData.name}
              onChange={handleChange}
              error={!!errors.name}
              helperText={errors.name || "Ej: Caja Principal"}
              placeholder="Ej: Caja Principal"
              InputProps={{
                startAdornment: <InputAdornment position="start"><PointOfSale /></InputAdornment>,
              }}
            />
          </Grid>

          {/* ID Externo (auto-generado) - Solo lectura */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size='small'
              label="ID Externo (auto-generado)"
              name="external_id"
              value={formData.external_id}
              InputProps={{ readOnly: true }}
              disabled
              helperText="Se genera automáticamente desde el nombre"
            />
          </Grid>
        </Grid>

        {/* Información adicional */}
        {/* <Alert severity="info" sx={{ mt: 3, mb: 2 }}>
          <strong>📌 Información importante:</strong>
          <ul style={{ margin: '8px 0 0 20px', padding: 0 }}>
            <li>Las cajas para QR son siempre de tipo <strong>Genérica</strong> y <strong>Monto fijo</strong></li>
            <li>Estos valores se configuran automáticamente según requerimientos de Mercado Pago</li>
            <li>La caja quedará vinculada a la sucursal seleccionada</li>
          </ul>
        </Alert> */}

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
          <Button variant="outlined" onClick={onCancel} disabled={submitting}>
            Cancelar
          </Button>
          <Button 
            type="submit" 
            variant="contained" 
            disabled={submitting}
            sx={{ bgcolor: '#009ee3', '&:hover': { bgcolor: '#0080b3' } }}
          >
            {submitting ? 'Creando...' : 'Crear Caja'}
          </Button>
        </Box>
      </form>
    </Paper>
  );
};

export default CajaForm;